import React, { createContext, useContext, useEffect, useMemo, useState } from 'react';

const ThemeContext = createContext({
  theme: 'light',
  setTheme: () => {},
  toggleTheme: () => {},
  customThemes: [],
  saveCustomTheme: () => {},
  deleteCustomTheme: () => {},
});

const VALID_BUILTIN = ['light', 'dark', 'urban-orange', 'urban-dark'];

const THEME_PAIRS = {
  light: 'dark', dark: 'light',
  'urban-orange': 'urban-dark', 'urban-dark': 'urban-orange',
};

const CUSTOM_THEMES_KEY = 'dp_custom_themes';

function hexToRgb(hex) {
  const h = (hex || '#000000').replace('#', '');
  const full = h.length === 3 ? h.split('').map(c => c + c).join('') : h;
  return {
    r: parseInt(full.slice(0, 2), 16) || 0,
    g: parseInt(full.slice(2, 4), 16) || 0,
    b: parseInt(full.slice(4, 6), 16) || 0,
  };
}

function clamp(v) { return Math.max(0, Math.min(255, Math.round(v))); }

function rgbToHex(r, g, b) {
  return '#' + [r, g, b].map(v => clamp(v).toString(16).padStart(2, '0')).join('');
}

function buildCustomThemeCss(t) {
  const isDark = Boolean(t.isDark);
  const primary     = t.primary     || (isDark ? '#818cf8' : '#6366f1');
  const bgPage      = t.bgPage      || (isDark ? '#0f0f0f' : '#f8f8f8');
  const bgCard      = t.bgCard      || (isDark ? '#1a1a1a' : '#ffffff');
  const textDark    = t.textDark    || (isDark ? '#f0f0f0' : '#1a1a1a');
  const borderColor = t.borderColor || (isDark ? '#2a2a2a' : '#e0e0e0');

  const { r: pr, g: pg, b: pb } = hexToRgb(primary);
  const primaryDark = rgbToHex(pr * 0.82, pg * 0.82, pb * 0.82);
  const primaryGlow = `rgba(${pr},${pg},${pb},${isDark ? 0.2 : 0.15})`;

  const { r: tr, g: tg, b: tb } = hexToRgb(textDark);
  const { r: br, g: bgr, b: bb } = hexToRgb(borderColor);
  const borderSubtle = `rgba(${br},${bgr},${bb},0.5)`;

  const { r: cr, g: cg, b: cb } = hexToRgb(bgCard);
  const bgCardHover = isDark
    ? rgbToHex(cr + 12, cg + 12, cb + 12)
    : rgbToHex(cr - 5, cg - 5, cb - 5);

  const luminance = (0.299 * pr + 0.587 * pg + 0.114 * pb) / 255;
  const textOnPrimary = luminance > 0.55 ? '#000000' : '#ffffff';

  return `[data-theme="custom"] {
  --primary-color: ${primary};
  --primary-dark: ${primaryDark};
  --primary-glow: ${primaryGlow};
  --secondary-color: ${primary};
  --secondary-glow: ${primaryGlow};
  --danger-color: ${isDark ? '#f87171' : '#ef4444'};
  --danger-glow: ${isDark ? 'rgba(248,113,113,0.2)' : 'rgba(239,68,68,0.15)'};
  --warning-color: ${isDark ? '#fbbf24' : '#d97706'};
  --warning-bg: ${isDark ? 'rgba(251,191,36,0.12)' : '#fef3c7'};
  --success-color: ${isDark ? '#4ade80' : '#16a34a'};
  --success-bg: ${isDark ? 'rgba(74,222,128,0.12)' : '#dcfce7'};
  --bg-page: ${bgPage};
  --bg-card: ${bgCard};
  --bg-card-hover: ${bgCardHover};
  --bg-subtle: ${bgCard};
  --bg-inset: ${isDark ? '#050505' : '#efefef'};
  --bg-overlay: ${bgCard};
  --glass-bg: ${bgCard};
  --glass-bg-strong: ${bgCardHover};
  --glass-bg-subtle: ${bgPage};
  --glass-border: ${borderColor};
  --glass-border-subtle: ${borderSubtle};
  --glass-blur: none;
  --glass-blur-sm: none;
  --bg-light: ${bgCard};
  --bg-dark: ${bgPage};
  --surface: ${bgCard};
  --surface-2: ${bgCardHover};
  --card-bg: ${bgCard};
  --bg-secondary: ${bgCard};
  --text-dark: ${textDark};
  --text-light: rgba(${tr},${tg},${tb},0.65);
  --text-muted: rgba(${tr},${tg},${tb},0.45);
  --text-on-primary: ${textOnPrimary};
  --text-primary: ${textDark};
  --text-secondary: rgba(${tr},${tg},${tb},0.65);
  --border-color: ${borderColor};
  --border-subtle: ${borderSubtle};
  --shadow-sm: ${isDark ? `0 0 0 1px ${borderColor}` : `0 1px 2px rgba(0,0,0,0.06),0 0 0 1px ${borderColor}`};
  --shadow-md: ${isDark ? `0 3px 6px rgba(0,0,0,0.4),0 0 0 1px ${borderColor}` : `0 3px 8px ${primaryGlow},0 0 0 1px ${borderColor}`};
  --shadow-lg: ${isDark ? '0 8px 24px rgba(0,0,0,0.5)' : `0 8px 24px ${primaryGlow}`};
  --shadow-xl: ${isDark ? '0 12px 40px rgba(0,0,0,0.6)' : `0 12px 40px ${primaryGlow}`};
  --body-gradient: ${bgPage};
  color-scheme: ${isDark ? 'dark' : 'light'};
}`;
}

export const ThemeProvider = ({ children }) => {
  const [customThemes, setCustomThemes] = useState(() => {
    try { return JSON.parse(localStorage.getItem(CUSTOM_THEMES_KEY) || '[]'); }
    catch { return []; }
  });

  const [theme, setTheme] = useState(() => {
    if (typeof window === 'undefined') return 'light';
    const saved = localStorage.getItem('theme');
    if (saved) return saved;
    return window.matchMedia?.('(prefers-color-scheme: dark)').matches ? 'dark' : 'light';
  });

  useEffect(() => {
    const mq = window.matchMedia?.('(prefers-color-scheme: dark)');
    if (!mq) return;
    const handler = (e) => {
      if (!localStorage.getItem('theme')) setTheme(e.matches ? 'dark' : 'light');
    };
    mq.addEventListener('change', handler);
    return () => mq.removeEventListener('change', handler);
  }, []);

  useEffect(() => {
    if (typeof document === 'undefined') return;
    const isBuiltin = VALID_BUILTIN.includes(theme);

    if (isBuiltin) {
      document.documentElement.setAttribute('data-theme', theme);
      const el = document.getElementById('dp-custom-theme');
      if (el) el.textContent = '';
    } else {
      const ct = customThemes.find(t => t.id === theme);
      if (ct) {
        document.documentElement.setAttribute('data-theme', 'custom');
        let el = document.getElementById('dp-custom-theme');
        if (!el) {
          el = document.createElement('style');
          el.id = 'dp-custom-theme';
          document.head.appendChild(el);
        }
        el.textContent = buildCustomThemeCss(ct);
      }
    }
    try { localStorage.setItem('theme', theme); } catch {}
  }, [theme, customThemes]);

  const saveCustomTheme = (themeData) => {
    const id = themeData.id || `custom-${Date.now()}`;
    const newTheme = { ...themeData, id };
    setCustomThemes(prev => {
      const updated = prev.some(t => t.id === id)
        ? prev.map(t => t.id === id ? newTheme : t)
        : [...prev, newTheme];
      try { localStorage.setItem(CUSTOM_THEMES_KEY, JSON.stringify(updated)); } catch {}
      return updated;
    });
    return id;
  };

  const deleteCustomTheme = (id) => {
    setCustomThemes(prev => {
      const updated = prev.filter(t => t.id !== id);
      try { localStorage.setItem(CUSTOM_THEMES_KEY, JSON.stringify(updated)); } catch {}
      return updated;
    });
    setTheme(t => (t === id ? 'light' : t));
  };

  const value = useMemo(() => ({
    theme,
    setTheme,
    toggleTheme: () => setTheme(t => {
      if (THEME_PAIRS[t]) return THEME_PAIRS[t];
      return 'light';
    }),
    customThemes,
    saveCustomTheme,
    deleteCustomTheme,
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }), [theme, customThemes]);

  return <ThemeContext.Provider value={value}>{children}</ThemeContext.Provider>;
};

export const useTheme = () => useContext(ThemeContext);
